// @flow
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga4';
import { useLocation, useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getOrgIdAndPropertyIdFromURL } from 'utils';
import {
  HOUSEHOLD_TYPE_AFFORDABLE,
  HOUSEHOLD_TYPE_CONVENTIONAL,
  HOUSEHOLD_TYPE_MIXED,
} from 'utils/constants/household';
import { BEDROOM_OPTIONS } from 'components/Organisms/WidgetMyUnit/constants';
import type { AppContextType, MarketingInfo } from 'types/context';
import { getLocalAuth } from '../utils/auth';
import { PAYMENT_METHODS } from 'utils/constants/paymentMethods';
import { useIntl } from 'react-intl';
import STUDENT_STATUS from 'constants/studentStatus';
import useApplicantTypesQuery from 'hooks/queries/useApplicantTypesQuery';
import useAppContextQuery from 'hooks/queries/useAppContextQuery';
import DatadogErrorBoundary from 'components/Atoms/DatadogErrorBoundary';
import {
  installGoogleTagManager,
  isGoogleTagManagerTrackingId,
} from 'utils/googleTagManager';

export const getTextingPhoneNumber = (
  phoneNumbers: { campaign: string, phoneNumber: string }[],
): ?string => {
  return (
    phoneNumbers?.find((p) => p.campaign === 'Customer Care')?.phoneNumber ??
    null
  );
};
export const AppContext: AppContextType = React.createContext();

export const useAppContext = () => {
  const context = React.useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppContextProvider');
  }
  return context;
};

export const AppContextProvider = ({ children }) => {
  const { authToken } = getLocalAuth();
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const language = navigator.language.split(/[-_]/)[0];

  const {
    authenticationPortal,
    assigningUnitsAtApplication,
    paymentsV1,
    uploadDocumentsInPortal,
    displayExecutedLeaseInPortal,
    propertySettingApplyWithoutAUnit,
    assigningUnitsMoveInDates,
    paymentsAutopay,
    assetsTaxRefund,
  } = useFlags();
  /**
   * Initial AppContext from `pathname`
   */

  const { data: applicantTypes } = useApplicantTypesQuery({
    enabled: authToken?.length > 0,
  });

  const { organizationId: urlOrganizationId, propertyId } = useMemo(() => {
    return getOrgIdAndPropertyIdFromURL(location.pathname, location.search);
  }, [location.pathname, location.search]);

  // State to track API errors and loading
  const [apiError, setApiError] = useState(null);

  // updated in WidgetMyApplication
  const [applicationFormComplete, setApplicationFormComplete] = useState(false);

  // Track the verified organizationId from the API response
  const [verifiedOrganizationId, setVerifiedOrganizationId] =
    useState(urlOrganizationId);

  const [appContext, setAppContext] = useState({
    organizationId: urlOrganizationId,
    propertyId,
    marketingInfo: { propertyClass: null, textingPhoneNumber: null },
    ageOfMajority: null,
    configs: {
      applyWithoutUnit: false,
      isUnitSelectionEnabled: false,
      isApplicationsOpen: false,
    },
    features: {
      isUploadDocumentsInPortalEnabled: false,
      isAssigningUnitsAtApplicationEnabled: false,
      isPaymentsV1Enabled: false,
      isAssigningUnitsMoveInDatesEnabled: false,
      propertySettingApplyWithoutAUnitFlag: false,
      paymentsAutopayFlag: false,
    },
    vehicleOptions: { colors: [], makes: [], types: [] },
    petTypes: [],
    studentTypes: [],
    relationships: [],
    applicantTypes: [],
    countriesOptions: [],
    dataLoaded: false,
  });

  const { numberOfBedsOptions } = appContext;

  const nBedSelectOptions = useMemo(() => {
    return BEDROOM_OPTIONS.filter((brOption) =>
      numberOfBedsOptions?.some((nBed) => Number(brOption.value) === nBed),
    );
  }, [numberOfBedsOptions]);

  // Update LaunchDarkly context when organizationId changes
  const updateLaunchDarklyContext = useCallback(
    (newOrganizationId) => {
      if (window.ldClient && typeof window.ldClient.identify === 'function') {
        const userContext = {
          kind: 'user',
          key: 'portalUser',
          propertyId,
          organizationId: newOrganizationId,
        };
        window.ldClient.identify(userContext);
      }
    },
    [propertyId],
  );

  // Function to update URL without page reload
  const updateURLWithCorrectOrganizationId = useCallback(
    (correctOrganizationId) => {
      const currentPath = location.pathname;
      const newPath = currentPath.replace(
        `/${urlOrganizationId}/`,
        `/${correctOrganizationId}/`,
      );

      if (newPath !== currentPath) {
        const fullNewPath = newPath + location.search;
        history.replace(fullNewPath);
      }
    },
    [history, location.pathname, location.search, urlOrganizationId],
  );

  // Fetch app context using React Query
  const {
    data: portalAppContext,
    isLoading: isLoadingAppContext,
    isSuccess,
    isError,
    error,
    dataUpdatedAt,
  } = useAppContextQuery({
    organizationId: urlOrganizationId,
    propertyId,
    enabled: Boolean(urlOrganizationId && propertyId),
  });

  // Handle successful app context fetch
  useEffect(() => {
    if (!isSuccess || !portalAppContext) return;

    setApiError(null);

    // Extract organizationId from the API response
    const responseOrganizationId = portalAppContext?.organizationId;

    // Verify organizationId matches
    if (
      responseOrganizationId &&
      responseOrganizationId !== urlOrganizationId
    ) {
      // Organization ID mismatch - report to Datadog and update with correct one
      const errorMessage = `Organization ID mismatch detected. User accessed with incorrect organizationId in URL.`;

      // Report error to Datadog
      DatadogErrorBoundary.reportError(new Error(errorMessage), {
        source: 'organization-id-mismatch',
        urlOrganizationId,
        correctOrganizationId: responseOrganizationId,
        propertyId,
        currentUrl: window.location.href,
        userAgent: navigator.userAgent,
        severity: 'warning',
      });

      // Update the verified organizationId
      setVerifiedOrganizationId(responseOrganizationId);

      // Update URL without page reload
      updateURLWithCorrectOrganizationId(responseOrganizationId);

      // Update LaunchDarkly context
      updateLaunchDarklyContext(responseOrganizationId);
    } else {
      setVerifiedOrganizationId(urlOrganizationId);
    }

    // GTM containers require the install snippets; GA4 IDs use ReactGA.
    const trackingId = portalAppContext?.googleAnalyticsTracking?.trackingId;
    if (isGoogleTagManagerTrackingId(trackingId)) {
      installGoogleTagManager(trackingId);
    } else if (trackingId && !ReactGA.isInitialized) {
      ReactGA.initialize([
        {
          trackingId,
          gtagOptions: {
            cookie_flags: 'SameSite=None;Secure',
          },
        },
      ]);
    }
  }, [
    isSuccess,
    portalAppContext,
    dataUpdatedAt,
    urlOrganizationId,
    updateURLWithCorrectOrganizationId,
    updateLaunchDarklyContext,
  ]);

  // Handle app context fetch error
  useEffect(() => {
    if (!isError || !error) return;

    const errorMessage =
      error.message || error.error || 'Failed to fetch property details';

    // Determine if this is an invalid property error before throwing
    const isInvalidProperty =
      errorMessage?.includes('404') ||
      errorMessage?.includes('not found') ||
      errorMessage?.includes('Property not found') ||
      errorMessage?.includes('400') ||
      error.statusCode === 400 ||
      error.statusCode === 404;

    // Create error with isInvalidProperty information
    const finalError = new Error(errorMessage);
    finalError.isInvalidProperty = isInvalidProperty;

    // Set error state for display
    setApiError({
      message: finalError.message || 'Failed to load property information',
      isInvalidProperty: isInvalidProperty,
    });
  }, [isError, error]);

  // Update app context when data is fetched
  useEffect(() => {
    // Extract organizationId from the API response
    const responseOrganizationId = portalAppContext?.organizationId;

    if (portalAppContext) {
      setAppContext((prevAppContext) => ({
        ...prevAppContext,
        ...portalAppContext,
        // Use the verified organizationId (either from response or URL if no mismatch)
        organizationId: responseOrganizationId || urlOrganizationId,
        relationships:
          portalAppContext?.relationships?.map((relationship) => ({
            ...relationship,
            translation: relationship.translations[language],
          })) ?? [],
        studentTypes: portalAppContext?.studentTypes?.map((studentType) => ({
          ...studentType,
          translation:
            intl.formatMessage({
              id: `app.studentStatus.${studentType.value}`,
            }) ?? [],
          ...(studentType.value ===
            STUDENT_STATUS.FULL_TIME_POST_K_12.value && {
            translationAdult: intl.formatMessage({
              id: `app.studentStatus.${studentType.value}.adult`,
            }),
          }),
        })),
        countriesOptions:
          portalAppContext?.countries?.map(({ id, name }) => ({
            value: id,
            text: name,
          })) ?? [],
        marketingInfo: {
          ...portalAppContext.marketingInfo,
          textingPhoneNumber: getTextingPhoneNumber(
            portalAppContext?.phoneNumbers,
          ),
        },
        dataLoaded: true,
      }));
    }
  }, [portalAppContext, intl, language, urlOrganizationId]);

  useEffect(() => {
    if (ReactGA.isInitialized) {
      ReactGA.send({ hitType: 'pageview', page: location.pathname });
    }
  }, [location.pathname]);

  const marketingInfo: MarketingInfo = appContext?.marketingInfo ?? {};

  const isConventionalProperty = Boolean(
    marketingInfo?.propertyClass === HOUSEHOLD_TYPE_CONVENTIONAL,
  );
  const isAffordableProperty = Boolean(
    marketingInfo?.propertyClass === HOUSEHOLD_TYPE_AFFORDABLE,
  );
  const isMixedProperty = Boolean(
    marketingInfo?.propertyClass === HOUSEHOLD_TYPE_MIXED,
  );

  const hasSomeOrNoCommercialFloorPlans = ['SOME', 'NONE'].includes(
    marketingInfo?.hasCommercialFloorPlans,
  );

  const isUnitSelectionEnabled =
    assigningUnitsAtApplication &&
    isConventionalProperty &&
    hasSomeOrNoCommercialFloorPlans
      ? appContext?.configs?.isUnitSelectionEnabled ?? false
      : false;

  // to help manage feature flagged features
  const features = {
    isAssigningUnitsAtApplicationEnabled: isUnitSelectionEnabled,
    isAuthenticationPortalEnabled: authenticationPortal,
    isUploadDocumentsInPortalEnabled: uploadDocumentsInPortal,
    isPaymentsV1Enabled: paymentsV1,
    displayExecutedLeaseInPortal,
    isAssigningUnitsMoveInDatesEnabled: assigningUnitsMoveInDates,
    propertySettingApplyWithoutAUnitFlag: propertySettingApplyWithoutAUnit,
    assigningUnitsAtApplicationFlag: assigningUnitsAtApplication,
    paymentsAutopayFlag: paymentsAutopay,
    showTaxRefundAsset: assetsTaxRefund,
  };

  const paymentProviderName =
    appContext?.paymentProvider?.paymentProvider ?? null;
  const showFortressPaymentMethods =
    features.isPaymentsV1Enabled &&
    paymentProviderName === PAYMENT_METHODS.FORTRESS;
  const isDomusoActive = marketingInfo?.isDomusoActive ?? false;
  const isPayOnlineAvailable =
    isDomusoActive !== marketingInfo?.isPayLeaseActive;
  const hasPaymentProvider = isPayOnlineAvailable || showFortressPaymentMethods;
  const propertySettings = {
    isUnitSelectionEnabled:
      appContext?.configs?.isUnitSelectionEnabled ?? false,
    isApplyWithoutUnitEnabled: appContext?.configs?.applyWithoutUnit ?? false,
  };

  const isUnitRequired = features.propertySettingApplyWithoutAUnitFlag
    ? propertySettings.isApplyWithoutUnitEnabled === false
    : true; // To avoid issues if flag is off

  const defaultContext = useMemo(
    () => ({
      ...appContext,
      applicantTypes,
      language,
      applicationFormComplete,
      authenticationPortal,
      features,
      isAffordableProperty,
      isConventionalProperty,
      isMixedProperty,
      organizationId: verifiedOrganizationId, // Use verified organizationId
      propertyId,
      ageOfMajority: appContext.ageOfMajority ?? 18,
      setAppContext,
      setApplicationFormComplete,
      isUnitRequired,
      nBedSelectOptions,
      propertySettings,
      paymentProviderName,
      showFortressPaymentMethods,
      isPayOnlineAvailable,
      hasPaymentProvider,
      isDomusoActive,
      // Add error state and loading state to context
      apiError,
      isLoadingAppContext,
    }),
    [
      appContext,
      applicantTypes,
      language,
      applicationFormComplete,
      authenticationPortal,
      features,
      isAffordableProperty,
      isConventionalProperty,
      isMixedProperty,
      verifiedOrganizationId,
      propertyId,
      setAppContext,
      setApplicationFormComplete,
      isUnitRequired,
      nBedSelectOptions,
      propertySettings,
      paymentProviderName,
      showFortressPaymentMethods,
      isPayOnlineAvailable,
      hasPaymentProvider,
      isDomusoActive,
      apiError,
      isLoadingAppContext,
    ],
  );

  return (
    <AppContext.Provider value={defaultContext}>{children}</AppContext.Provider>
  );
};

AppContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default { AppContext, AppContextProvider };
